import React, { useState, useEffect, useRef } from 'react';
import {
  FaCircle, FaSignInAlt, FaUserPlus, FaExclamationTriangle,
  FaCalendarAlt, FaSyncAlt, FaStore, FaChartBar,
} from 'react-icons/fa';
import { supabase } from '../services/supabase';

// ─── helpers ────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s əvvəl`;
  if (diff < 3600) return `${Math.floor(diff / 60)}dəq əvvəl`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}s əvvəl`;
  return new Date(ts).toLocaleDateString('az-AZ');
}

function daysLeft(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - Date.now()) / 86400000);
}

// ─── StatBox ─────────────────────────────────────────────────────────────────
function StatBox({ label, value, icon: Icon, color, sub }) {
  return (
    <div style={{ ...s.statBox, borderTop: `4px solid ${color}` }}>
      <div style={s.statIcon(color)}><Icon size={20} /></div>
      <div>
        <div style={s.statValue}>{value ?? '—'}</div>
        <div style={s.statLabel}>{label}</div>
        {sub && <div style={s.statSub}>{sub}</div>}
      </div>
    </div>
  );
}

// ─── LiveBadge ───────────────────────────────────────────────────────────────
function LiveBadge() {
  const [tick, setTick] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setTick(t => !t), 800);
    return () => clearInterval(id);
  }, []);
  return (
    <span style={s.liveBadge}>
      <FaCircle size={8} style={{ color: tick ? '#22c55e' : '#16a34a', transition: 'color 0.4s' }} />
      CANLI
    </span>
  );
}

export default function MonitoringPage() {
  const [todayStats, setTodayStats] = useState({ checkIns: 0, signups: 0, fraudAlerts: 0, activeBiz: 0 });
  const [liveStream, setLiveStream] = useState([]);
  const [expiringBiz, setExpiringBiz] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchAll();
    subscribeRealtime();
    const timer = setInterval(fetchTodayStats, 30000);
    return () => {
      clearInterval(timer);
      if (streamRef.current) streamRef.current.unsubscribe();
    };
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchTodayStats(), fetchExpiringBiz(), fetchRecentSignups()]);
    setLoading(false);
    setLastRefresh(new Date());
  };

  const fetchTodayStats = async () => {
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const iso = todayStart.toISOString();

    const [{ count: checkIns }, { count: signups }, { count: fraudAlerts }, { count: activeBiz }] = await Promise.all([
      supabase.from('check_ins').select('*', { count: 'exact', head: true }).gte('created_at', iso),
      supabase.from('users').select('*', { count: 'exact', head: true }).gte('created_at', iso),
      supabase.from('fraud_logs').select('*', { count: 'exact', head: true }).eq('resolved', false),
      supabase.from('businesses').select('*', { count: 'exact', head: true }).eq('is_active', true),
    ]);
    setTodayStats({ checkIns: checkIns || 0, signups: signups || 0, fraudAlerts: fraudAlerts || 0, activeBiz: activeBiz || 0 });
    setLastRefresh(new Date());
  };

  const fetchExpiringBiz = async () => {
    const now = new Date().toISOString();
    const in30 = new Date(Date.now() + 30 * 86400000).toISOString();
    const { data } = await supabase
      .from('businesses')
      .select('id, name, subscription_end, owner_id')
      .eq('is_active', true)
      .gte('subscription_end', now)
      .lte('subscription_end', in30)
      .order('subscription_end', { ascending: true })
      .limit(20);
    setExpiringBiz(data || []);
  };

  const fetchRecentSignups = async () => {
    const { data } = await supabase
      .from('users')
      .select('id, full_name, email, created_at, user_type')
      .order('created_at', { ascending: false })
      .limit(15);
    setRecentSignups(data || []);
  };

  const subscribeRealtime = () => {
    streamRef.current = supabase
      .channel('monitoring-checkins')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'check_ins' }, payload => {
        setLiveStream(prev => [
          { ...payload.new, _type: 'checkin', _ts: new Date() },
          ...prev.slice(0, 49),
        ]);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'users' }, payload => {
        setLiveStream(prev => [
          { ...payload.new, _type: 'signup', _ts: new Date() },
          ...prev.slice(0, 49),
        ]);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'fraud_logs' }, payload => {
        setLiveStream(prev => [
          { ...payload.new, _type: 'fraud', _ts: new Date() },
          ...prev.slice(0, 49),
        ]);
      })
      .subscribe();
  };

  if (loading) return <div style={s.loading}>Yüklənir...</div>;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Monitorinq <LiveBadge /></h1>
          {lastRefresh && (
            <p style={s.sub}>Son yeniləmə: {lastRefresh.toLocaleTimeString('az-AZ')} · 30s-dən bir avtomatik</p>
          )}
        </div>
        <button onClick={fetchAll} style={s.refreshBtn}>
          <FaSyncAlt size={14} /> Yenilə
        </button>
      </div>

      {/* Today stats */}
      <div style={s.statsRow}>
        <StatBox label="Check-in (bugün)" value={todayStats.checkIns} icon={FaSignInAlt} color="#6366f1" sub="saat 00:00-dan" />
        <StatBox label="Yeni qeydiyyat" value={todayStats.signups} icon={FaUserPlus} color="#22c55e" sub="bugün" />
        <StatBox label="Açıq fraud xəbərdarlığı" value={todayStats.fraudAlerts} icon={FaExclamationTriangle} color="#ef4444" sub="həll edilməmiş" />
        <StatBox label="Aktiv biznes" value={todayStats.activeBiz} icon={FaStore} color="#f59e0b" sub="platform üzrə" />
      </div>

      <div style={s.twoCol}>
        {/* Live stream */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Canlı Axın <LiveBadge /></h2>
            <span style={s.countBadge}>{liveStream.length} hadisə</span>
          </div>
          {liveStream.length === 0 ? (
            <div style={s.empty}>
              <FaCircle size={12} style={{ color: '#6b7280', marginBottom: 8 }} />
              <p>Hələ hadisə yoxdur. Real-time gözlənilir...</p>
            </div>
          ) : (
            <div style={s.streamList}>
              {liveStream.map((ev, i) => (
                <div key={i} style={{ ...s.streamItem, borderLeft: `3px solid ${ev._type === 'checkin' ? '#6366f1' : ev._type === 'signup' ? '#22c55e' : '#ef4444'}` }}>
                  <div style={s.streamIcon(ev._type)}>
                    {ev._type === 'checkin' ? <FaSignInAlt size={11} /> : ev._type === 'signup' ? <FaUserPlus size={11} /> : <FaExclamationTriangle size={11} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={s.streamTitle}>
                      {ev._type === 'checkin' && `Check-in — istifadəçi ${ev.user_id?.slice(0, 8)}...`}
                      {ev._type === 'signup' && `Yeni qeydiyyat — ${ev.email || ev.full_name || 'İstifadəçi'}`}
                      {ev._type === 'fraud' && `⚠️ Fraud xəbərdarlığı — ${ev.fraud_type || 'naməlum'}`}
                    </div>
                    <div style={s.streamTime}>{timeAgo(ev._ts)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Expiring subscriptions */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h2 style={s.cardTitle}><FaCalendarAlt size={14} /> Bitən Abunəliklər</h2>
              <span style={s.countBadge}>{expiringBiz.length} biznes</span>
            </div>
            {expiringBiz.length === 0 ? (
              <div style={s.empty}><p>30 gün ərzində bitən abunəlik yoxdur</p></div>
            ) : (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Biznes</th>
                      <th style={s.th}>Bitmə tarixi</th>
                      <th style={s.th}>Qalır</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiringBiz.map(biz => {
                      const days = daysLeft(biz.subscription_end);
                      const color = days <= 3 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#22c55e';
                      return (
                        <tr key={biz.id} style={s.tr}>
                          <td style={s.td}>{biz.name}</td>
                          <td style={s.td}>{new Date(biz.subscription_end).toLocaleDateString('az-AZ')}</td>
                          <td style={s.td}>
                            <span style={{ ...s.daysBadge, backgroundColor: color + '20', color }}>
                              {days <= 0 ? 'Bitib' : `${days} gün`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent signups */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h2 style={s.cardTitle}><FaUserPlus size={14} /> Son Qeydiyyatlar</h2>
            </div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Ad</th>
                    <th style={s.th}>Tip</th>
                    <th style={s.th}>Vaxt</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSignups.map(u => (
                    <tr key={u.id} style={s.tr}>
                      <td style={s.td}>{u.full_name || u.email || '—'}</td>
                      <td style={s.td}>
                        <span style={{ ...s.typeBadge, backgroundColor: u.user_type === 'visitor' ? '#3b82f620' : '#8b5cf620', color: u.user_type === 'visitor' ? '#3b82f6' : '#8b5cf6' }}>
                          {u.user_type || 'local'}
                        </span>
                      </td>
                      <td style={s.td}>{timeAgo(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Check-in activity by hour - today */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h2 style={s.cardTitle}><FaChartBar size={14} /> Bugünkü Check-in Saatlara Görə</h2>
        </div>
        <HourlyBar />
      </div>
    </div>
  );
}

// ─── HourlyBar ───────────────────────────────────────────────────────────────
function HourlyBar() {
  const [hourly, setHourly] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
      const { data } = await supabase
        .from('check_ins')
        .select('created_at')
        .gte('created_at', todayStart.toISOString());

      const counts = Array(24).fill(0);
      data?.forEach(ci => {
        const h = new Date(ci.created_at).getHours();
        counts[h]++;
      });
      setHourly(counts);
    };
    fetch();
  }, []);

  const max = Math.max(...hourly, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, padding: '0 8px' }}>
      {hourly.map((v, h) => (
        <div key={h} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ width: '100%', height: Math.max(4, (v / max) * 64), backgroundColor: v > 0 ? '#6366f1' : '#e5e7eb', borderRadius: 3, transition: 'height 0.3s' }} title={`${h}:00 — ${v} check-in`} />
          {h % 4 === 0 && <span style={{ fontSize: 9, color: '#9ca3af' }}>{h}:00</span>}
        </div>
      ))}
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────
const s = {
  page: { padding: 28, minHeight: '100vh', backgroundColor: '#f8fafc' },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', fontSize: 18, color: '#6366f1' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  sub: { fontSize: 13, color: '#64748b', marginTop: 4 },
  liveBadge: { display: 'inline-flex', alignItems: 'center', gap: 5, backgroundColor: '#dcfce7', color: '#16a34a', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  statBox: { backgroundColor: '#fff', borderRadius: 12, padding: 20, display: 'flex', gap: 14, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  statIcon: c => ({ width: 44, height: 44, borderRadius: 10, backgroundColor: c + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c }),
  statValue: { fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1 },
  statLabel: { fontSize: 12, color: '#64748b', marginTop: 4 },
  statSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 },
  card: { backgroundColor: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 0 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: 8 },
  countBadge: { backgroundColor: '#f1f5f9', color: '#475569', fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600 },
  empty: { textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 },
  streamList: { maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 },
  streamItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: 8 },
  streamIcon: t => ({ width: 28, height: 28, borderRadius: 7, backgroundColor: t === 'checkin' ? '#6366f115' : t === 'signup' ? '#22c55e15' : '#ef444415', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t === 'checkin' ? '#6366f1' : t === 'signup' ? '#22c55e' : '#ef4444', flexShrink: 0 }),
  streamTitle: { fontSize: 13, color: '#1e293b', fontWeight: 500 },
  streamTime: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #f1f5f9', fontSize: 12 },
  tr: { borderBottom: '1px solid #f8fafc' },
  td: { padding: '9px 10px', color: '#334155', verticalAlign: 'middle' },
  daysBadge: { padding: '3px 9px', borderRadius: 20, fontSize: 12, fontWeight: 700 },
  typeBadge: { padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
};
